"use client"

import { Box, HStack, Portal, Text, VStack } from "@chakra-ui/react"
import { useEffect, useState } from "react"

import { breakpoints } from "@/theme/breakpoints"

type ViewportSize = {
  height: number
  remBase: number
  width: number
}

const getBreakpointPixelValue = (value: string) => Number.parseFloat(value)
const breakpointEntries = Object.entries(breakpoints).map(([name, value]) => ({
  minWidth: getBreakpointPixelValue(value),
  name,
}))

const getViewportSize = (): ViewportSize => {
  const remBase = Number.parseFloat(
    window.getComputedStyle(document.documentElement).fontSize
  )

  return {
    height: window.innerHeight,
    remBase: Number.isFinite(remBase) && remBase > 0 ? remBase : 16,
    width: window.innerWidth,
  }
}

const formatRem = (value: number, remBase: number) =>
  `${Number.parseFloat((value / remBase).toFixed(2))}rem`

const getDisplayBreakpoint = (width: number) => {
  const twoXlWidth = getBreakpointPixelValue(breakpoints["2xl"])

  if (width > twoXlWidth) {
    return "2xl+"
  }

  return breakpointEntries.reduce(
    (currentBreakpoint, breakpoint) =>
      width >= breakpoint.minWidth ? breakpoint.name : currentBreakpoint,
    "base"
  )
}

const DevBreakpointIndicator = () => {
  const [viewportSize, setViewportSize] = useState<ViewportSize | null>(null)

  useEffect(() => {
    let animationFrame = 0

    const updateViewportSize = () => {
      window.cancelAnimationFrame(animationFrame)
      animationFrame = window.requestAnimationFrame(() => {
        setViewportSize(getViewportSize())
      })
    }

    updateViewportSize()
    window.addEventListener("resize", updateViewportSize)

    return () => {
      window.cancelAnimationFrame(animationFrame)
      window.removeEventListener("resize", updateViewportSize)
    }
  }, [])

  if (process.env.NODE_ENV !== "development" || !viewportSize) {
    return null
  }

  const displayBreakpoint = getDisplayBreakpoint(viewportSize.width)

  return (
    <Portal>
      <Box
        aria-hidden="true"
        bg="bg.inverted"
        border="1px solid"
        borderColor="border.inverted"
        bottom={{ base: 4, md: 6 }}
        boxShadow="lg"
        color="fg.inverted"
        data-dev-breakpoint-indicator=""
        left="50%"
        pointerEvents="none"
        position="fixed"
        px={3}
        py={2}
        rounded="sm"
        transform="translateX(-50%)"
        zIndex="max"
      >
        <HStack alignItems="baseline" gap={2}>
          <Text
            as="strong"
            color="fg.inverted"
            fontFamily="mono"
            fontSize="sm"
            fontWeight="800"
            lineHeight="1"
          >
            {displayBreakpoint}
          </Text>
          <VStack alignItems="flex-start" gap={0}>
            <Text
              color="fg.inverted"
              fontFamily="mono"
              fontSize="xs"
              lineHeight="1"
            >
              {viewportSize.width}px x {viewportSize.height}px
            </Text>
            {displayBreakpoint === "2xl+" && (
              <Text
                color="fg.inverted"
                fontFamily="mono"
                fontSize="2xs"
                lineHeight="1"
                pt={1}
              >
                {formatRem(viewportSize.width, viewportSize.remBase)} x{" "}
                {formatRem(viewportSize.height, viewportSize.remBase)}
              </Text>
            )}
          </VStack>
        </HStack>
      </Box>
    </Portal>
  )
}

export default DevBreakpointIndicator
